import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import React from "react";
import useLang from "../../../../hooks/useLang";

/**
 * The strip along the bottom edge of a record that has something to say about
 * merging — "duplicates exist, merge them" or "this came from a merge, here are
 * its sources" (EAI-1815 / EAI-1816).
 *
 * Four of these existed across the opportunity card and the lead row in three
 * different shapes: an amber bar with an antd Button, a blue bar with another
 * Button, and a dark slate bar with its copy hardcoded in JSX. They're one
 * component now, built to the design canvas spec ("Duplicate banner" /
 * "Merged banner" screens), so the two surfaces can't drift apart again.
 *
 * Spec, both tones: `padding: 4px 16px`, 8px gap, a 14px icon, and 13px/20px
 * text whose action is an **inline** link ending the sentence — not a Button,
 * which forced the strip ~12px taller for a control that reads as part of that
 * sentence.
 *
 * The one deliberate departure is the border. The canvas nests this inside the
 * card's own bordered wrapper, so `border-top` is all it needs there; here the
 * strip is a SIBLING that follows the card, so it carries the full 1px box in
 * its tone colour and matches the card's 4px bottom radius. `-mt-px` lands its
 * top edge on the card's bottom border rather than stacking into a 2px seam.
 */

const TONES = {
  /** Informational — this record is the product of a merge. */
  success: { bg: "#F6FFED", border: "#B7EB8F", icon: "#52C41A", Icon: CheckCircleFilled },
  /** Needs attention — unmerged duplicates still exist. */
  warning: { bg: "#FFFBE6", border: "#FFE58F", icon: "#FAAD14", Icon: ExclamationCircleFilled },
};

/** Sentence text — the design's contrast floor, not a grey token. */
const INK = "rgba(0,0,0,0.88)";
const LINK = "#1677FF";

/**
 * Background for the source records revealed by "Show merged records".
 * Deliberately not the banner's green: these are archived records, and pink
 * reads as "this is history" rather than "this succeeded".
 */
export const MERGED_PREVIEW_BG = "#FFF0F6";

/**
 * @param {"success"|"warning"} tone
 * @param {React.ReactNode} children - the sentence, minus its action.
 * @param {string} actionLabel - the inline link ending that sentence.
 * @param {boolean} [loading] - holds the link on "Loading" while a fetch runs.
 * @param {boolean} [ariaExpanded] - for a link that toggles something.
 */
const MergeBanner = ({
  tone = "success",
  children,
  actionLabel,
  onAction,
  loading,
  ariaExpanded,
  className = "",
}) => {
  const [lang] = useLang();
  const { bg, border, icon, Icon } = TONES[tone] || TONES.success;

  return (
    <div
      className={`-mt-px flex items-center gap-2 rounded-b-[4px] border border-solid px-4 py-1 ${className}`}
      style={{ background: bg, borderColor: border }}
    >
      <Icon className="flex-none" style={{ color: icon, fontSize: 14 }} />
      <span className="min-w-0 flex-1 text-[13px] leading-5" style={{ color: INK }}>
        {children}
        {actionLabel && (
          <>
            {" "}
            <button
              type="button"
              // Inline so it ends the sentence as the design has it, but still a
              // real button: Enter/Space work and any toggled state is announced.
              className={`border-0 bg-transparent p-0 align-baseline text-[13px] font-medium ${
                loading ? "cursor-default opacity-60" : "cursor-pointer"
              }`}
              style={{ color: LINK }}
              aria-expanded={ariaExpanded}
              disabled={loading}
              onClick={() => !loading && onAction?.()}
            >
              {loading ? `${lang.loading}...` : actionLabel}
            </button>
          </>
        )}
      </span>
    </div>
  );
};

/**
 * "Merged opportunity. Show merged records"
 *
 * Expanding appends the archived source records to the list around this one.
 * They arrive looking like ordinary rows, so the caller tints them
 * (`MERGED_PREVIEW_BG`) to keep what the merge swallowed distinct from live
 * records.
 */
export const MergedRecordsBanner = ({
  /** "Merged opportunity" / "Merged lead". */
  label,
  /** Are the source records currently listed? */
  expanded,
  /** The lead side fetches on expand; holds the link on "Loading". */
  loading,
  onToggle,
  className = "",
}) => {
  const [lang] = useLang();

  return (
    <MergeBanner
      tone="success"
      className={className}
      actionLabel={expanded ? lang.hideMergedRecords : lang.showMergedRecords}
      ariaExpanded={Boolean(expanded)}
      loading={loading}
      onAction={onToggle}
    >
      {`${label}.`}
    </MergeBanner>
  );
};

/**
 * "Duplicates exist for this lead. Merge" — shown while the BE still reports
 * unmerged duplicates on this record.
 */
export const DuplicateRecordsBanner = ({ label, onMerge, canMerge = true, className = "" }) => {
  const [lang] = useLang();

  return (
    <MergeBanner
      tone="warning"
      className={className}
      // No permission to merge: state the problem, offer no action.
      actionLabel={canMerge ? lang.mergeAction : null}
      onAction={onMerge}
    >
      {label}
    </MergeBanner>
  );
};

export default MergeBanner;
